import { useState, useCallback } from 'react'
import { Course } from '@mc/api'
import {
  TileCaption,
  IconAddDefault,
  Button,
  IconCloseDefault,
  TileOverlay,
} from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { useStaticData } from '@mc/static-data-react'
import { type OutcomeKeyLearnings } from 'libs/server/api/outcomes'
import { trackShowHideKeyLearningsClick } from '../../events'
import { CM_OUTCOMES_I18N_NAMESPACE } from '../../i18n'

const buttonStyles = {
  width: 48,
  height: 48,
}

export const useShowKeyLearnings = ({
  outcome,
  position,
}: {
  outcome: OutcomeKeyLearnings
  position: number
}) => {
  const { title, keyLearnings } = outcome
  const { course } = useStaticData<{ course: Course }>()
  const { t } = useTranslation(CM_OUTCOMES_I18N_NAMESPACE)
  const [showKeyLearnings, setShowKeyLearnings] = useState(false)

  const onClickCloseIcon = useCallback(() => {
    setShowKeyLearnings(false)
    trackShowHideKeyLearningsClick({
      action: 'close',
      courseSlug: course.slug,
      outcome,
      position,
    })
  }, [course.slug, outcome, position])

  const onClickPlusIcon = useCallback(() => {
    setShowKeyLearnings(true)
    trackShowHideKeyLearningsClick({
      action: 'open',
      courseSlug: course.slug,
      outcome,
      position,
    })
  }, [course.slug, outcome, position])

  const MainContent = () => (
    <TileCaption position='left bottom' padding={0}>
      <div className='mc-text--bold d-flex justify-content-between align-items-end mc-py-4 mc-px-5'>
        <div style={{ width: '85%' }}>
          <p className='mc-mb-2 mc-text-h7'>{t('outcomesOnCM.howTo')}</p>
          <p className='mc-text-h4 mc-text--3-lines-max'>{title}</p>
        </div>
        <Button
          kind='play'
          rounded
          className='mc-p-2'
          style={buttonStyles}
          onClick={onClickPlusIcon}
        >
          <IconAddDefault
            size='sm'
            srText={t('outcomesOnCM.keyLearnings.a11y.open')}
          />
        </Button>
      </div>
    </TileCaption>
  )

  const KeyLearningsContent = () => (
    <>
      <TileCaption position='left center' padding={0}>
        <div className='mc-py-4 mc-px-5'>
          {keyLearnings.map((learning) => (
            <p key={learning} className='mc-text--bold mc-mb-3'>
              {learning}
            </p>
          ))}
        </div>
      </TileCaption>
      <TileCaption position='right bottom' padding={0}>
        <div className='mc-py-4 mc-px-5'>
          <Button
            kind='secondary'
            rounded
            className='mc-p-2'
            style={buttonStyles}
            onClick={onClickCloseIcon}
          >
            <IconCloseDefault
              size='sm'
              srText={t('outcomesOnCM.keyLearnings.a11y.close')}
            />
          </Button>
        </div>
      </TileCaption>
    </>
  )

  const Content = () =>
    showKeyLearnings ? <KeyLearningsContent /> : <MainContent />

  const Overlay = () =>
    showKeyLearnings ? <TileOverlay type='solid' /> : <TileOverlay />

  return { Content, Overlay }
}
